import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { NgbDateParserFormatter, NgbDate, NgbInputDatepicker, NgbDateStruct, NgbDatepickerNavigateEvent } from '@ng-bootstrap/ng-bootstrap';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Util } from '@libs/utilities/util';

import { INgDatePickerOptions, INgDateRange } from '../shared/date-picker.interface';
import { NgAbstractInputComponent } from '../../shared/inputs.abstract';
import { TypeNgGenericForm } from '../../../../types/object-types.interface';
import { INgCustomDropdownOption } from '../../dropdowns/custom-dropdown/custom-dropdown.interface';
import { createMonthOptions, createYearOptions } from '../shared/date-navigation.util';


@Component({
    selector: 'ng-date-range-picker',
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['./date-range-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: NgDateRangePickerComponent,
            multi: true
        }
    ],
    standalone: false
})
export class NgDateRangePickerComponent extends NgAbstractInputComponent<INgDateRange> implements OnInit, ControlValueAccessor {

	private rangeState: { startDate: Date | null; endDate: Date | null } = {
		startDate: null,
		endDate: null
	};

	//** Element refs */
	@ViewChild('startDatepicker') startDatepicker!: NgbInputDatepicker;
	@ViewChild('endDatepicker') endDatepicker!: NgbInputDatepicker;

	//** Decorators */
	@Input() options	!: INgDatePickerOptions;
	@Input() showFieldLabels: boolean = false;
	@Input() startLabel	 : string = 'From';
	@Input() endLabel	 : string = 'To';
	@Input() formGroup	 : FormGroup<TypeNgGenericForm<INgDateRange>> = new FormGroup<TypeNgGenericForm<INgDateRange>>({
		startDate: new FormControl(new Date(new Date().setDate(new Date().getDate() - 1)), { nonNullable: true }),
		endDate	 : new FormControl(new Date(), { nonNullable: true })
	});

	//** UI Variables */
	hoveredDate	: NgbDateStruct | null = null;
	fromDate	: NgbDateStruct | null = null;
	toDate		: NgbDateStruct | null = null;
	activeCalendar: 'start' | 'end' | null = null;

	datepickerMinDate	: NgbDateStruct = null!;
	datepickerMaxDate	: NgbDateStruct = null!;

	//** Calendar navigation */
	readonly calendarMonthOptions: INgCustomDropdownOption<number>[] = createMonthOptions();
	calendarYearOptions: INgCustomDropdownOption<number>[] = createYearOptions();
	startCalendarMonth: number = new Date().getMonth() + 1;
	startCalendarYear: number = new Date().getFullYear();
	endCalendarMonth: number = new Date().getMonth() + 1;
	endCalendarYear: number = new Date().getFullYear();


	constructor(
		public  formatter	: NgbDateParserFormatter,
		private cdr			: ChangeDetectorRef,
		@Inject(DOCUMENT) private readonly document: Document
	) {
		super('date-range-picker');
	}

	get focusRestoreTarget(): HTMLElement {
		return this.document.body;
	}

	ngOnInit(): void {
		// Set min and max dates. Model values are applied silently through writeValue.
		if (this.options?.minDate) this.datepickerMinDate = this.prepareDateStruct(this.options.minDate!);
		if (this.options?.maxDate) this.datepickerMaxDate = this.prepareDateStruct(this.options.maxDate!);
	}

	onStartDateSelection(date: NgbDate): void {
		const keepEndDate: boolean = Boolean(this.toDate) && date.before(this.toDate!);
		this.applyState({
			startDate: Util.Date.fromNgbDate(date),
			endDate: keepEndDate ? this.rangeState.endDate : null
		}, true);
		this.startDatepicker.close();
	}

	onEndDateSelection(date: NgbDate): void {
		if (this.isEndDateDisabled(date)) return;
		this.applyState({
			startDate: this.rangeState.startDate,
			endDate: Util.Date.fromNgbDate(date)
		}, true);
		this.endDatepicker.close();
	}

	isHovered(date: NgbDate): boolean | null {
		return this.activeCalendar === 'end' && this.fromDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
	}

	isInside(date: NgbDate): boolean | null {
		return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
	}

	isRange(date: NgbDate): boolean | null {
		return (
			date.equals(this.fromDate)
			|| (this.toDate && date.equals(this.toDate))
			|| this.isInside(date)
			|| this.isHovered(date)
		);
	}

	syncCalendarNavigation(event: NgbDatepickerNavigateEvent, field: 'start' | 'end'): void {
		if (field === 'start') {
			this.startCalendarMonth = event.next.month;
			this.startCalendarYear = event.next.year;
		} else {
			this.endCalendarMonth = event.next.month;
			this.endCalendarYear = event.next.year;
		}
		this.calendarYearOptions = createYearOptions(this.options);
		this.cdr.markForCheck();
	}

	navigateCalendar(datepicker: NgbInputDatepicker, month: number, year: number): void {
		datepicker.navigateTo({ year, month, day: 1 });
	}

	toggleCalendar(datepicker: NgbInputDatepicker, otherDatepicker: NgbInputDatepicker, field: 'start' | 'end'): void {
		if (this.isDisabled) return;
		otherDatepicker.close();
		this.activeCalendar = field;
		if (datepicker.isOpen()) {
			datepicker.close();
			return;
		}
		datepicker.open();
		const selectedDate: NgbDateStruct | null = field === 'start'
			? this.fromDate
			: this.toDate ?? this.fromDate;
		if (selectedDate) datepicker.navigateTo(selectedDate);
	}

	onCalendarClosed(field: 'start' | 'end'): void {
		this.hoveredDate = null;
		if (this.activeCalendar === field) this.activeCalendar = null;
		this.onTouched();
	}

	isEndDateDisabled = (date: NgbDate): boolean => {
		if (!this.fromDate || !date.after(this.fromDate)) return true;
		if (!this.options?.disableDates?.length) return false;
		const value: Date = Util.Date.fromNgbDate(date);
		return this.options.disableDates.some((disabledDate: Date) => Util.Date.compareByDateString(value, disabledDate));
	};


	/**------------------------------------------------------
	 * Overwrites
	 */
	override writeValue(value: INgDateRange | null): void {
		this.applyState({
			startDate: value?.startDate ?? null,
			endDate: value?.endDate ?? null
		}, false);
	}

	override registerOnChange(fn: (value: INgDateRange) => void): void {
		this.onChange = fn;
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */
	private applyState(value: { startDate: Date | null; endDate: Date | null }, emitChange: boolean): void {
		this.rangeState = { ...value };
		this.fromDate = this.getNgbDateDate(this.rangeState.startDate);
		this.toDate = this.getNgbDateDate(this.rangeState.endDate);

		if (this.rangeState.startDate && this.rangeState.endDate) {
			this.value = {
				startDate: this.rangeState.startDate,
				endDate: this.rangeState.endDate
			};
			this.formGroup.setValue(this.value, { emitEvent: false });
			if (emitChange) this.onChange({ ...this.value });
		} else {
			this.value = null;
		}

		if (emitChange) this.onTouched();
		this.cdr.markForCheck();
	}

	private getNgbDateDate(date: Date | null) {
		if (!date) return null;
		return Util.Date.toNgbDate(date);
	}

	private prepareDateStruct(date: Date): NgbDateStruct {
		return {
			year	: date.getFullYear(),
			month	: date.getMonth() + 1,
			day		: date.getDate()
		};
	}
}
